import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import "./FloatingDashboardPedidos.css";

const POLL_MS = 8000;

function localDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function demandInfo(count) {
  if (count >= 10) return { key: "critical" };
  if (count >= 7) return { key: "danger" };
  if (count >= 4) return { key: "warning" };
  return { key: "success" };
}

function fmtMoney(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function textOrDash(value) {
  const text = String(value ?? "").trim();
  return text || "-";
}

function isPaid(value) {
  return value === true || String(value).toLowerCase() === "true";
}

function paymentText(pedido) {
  if (Array.isArray(pedido?.pagos) && pedido.pagos.length) {
    return pedido.pagos
      .map((p) => `${textOrDash(p.medio)} ${isPaid(p.pagado) ? "pagado" : "pendiente"}`)
      .join(" / ");
  }
  if (pedido?.medioPago) return `${pedido.medioPago} ${isPaid(pedido.pagado) ? "pagado" : "pendiente"}`;
  return isPaid(pedido?.pagado) ? "Pagado" : "Pendiente";
}

function comandaLabel(pedido) {
  return `Comanda #${pedido?.numeroComanda ?? pedido?.numero_comanda ?? pedido?.id ?? "-"}`;
}

function normalizePedido(pedido, fallback = {}) {
  return {
    ...fallback,
    ...pedido,
    cliente: pedido?.cliente ?? fallback?.cliente ?? "",
    numeroComanda: pedido?.numeroComanda ?? pedido?.numero_comanda ?? fallback?.numeroComanda ?? fallback?.numero_comanda ?? null,
    telefono: pedido?.telefono ?? fallback?.telefono ?? "",
    canal: pedido?.canal ?? fallback?.canal ?? "",
    horaRetiro: pedido?.horaRetiro ?? pedido?.hora_retiro ?? fallback?.horaRetiro ?? "",
    observacion: pedido?.observacion ?? fallback?.observacion ?? "",
    items: Array.isArray(pedido?.items) ? pedido.items : Array.isArray(fallback?.items) ? fallback.items : [],
    total: Number(pedido?.total ?? fallback?.total ?? 0),
    totalNeto: Number(pedido?.totalNeto ?? pedido?.total_neto ?? fallback?.totalNeto ?? fallback?.total ?? 0),
    medioPago: pedido?.medioPago ?? pedido?.medio_pago ?? fallback?.medioPago ?? "",
    pagos: Array.isArray(pedido?.pagos) ? pedido.pagos : Array.isArray(fallback?.pagos) ? fallback.pagos : [],
    pagado: isPaid(pedido?.pagado ?? fallback?.pagado),
  };
}

function selectorDate(mode, customDate) {
  if (mode === "tomorrow") return localDate(1);
  if (mode === "custom") return customDate || localDate(0);
  return localDate(0);
}

export default function FloatingDashboardPedidos({ open, onClose }) {
  const [mode, setMode] = useState("today");
  const [customDate, setCustomDate] = useState(localDate(0));
  const [data, setData] = useState({ grupos: [], totalPedidos: 0, actualizadoEn: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedHora, setSelectedHora] = useState(null);
  const [selectedPedidoId, setSelectedPedidoId] = useState(null);
  const [pedidoDetails, setPedidoDetails] = useState({});

  const fecha = selectorDate(mode, customDate);

  const cargar = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const { data: res } = await api.get("/api/pedidos/dashboard", { params: { fecha } });
      setData({
        grupos: Array.isArray(res?.grupos) ? res.grupos : [],
        pedidos: Array.isArray(res?.pedidos) ? res.pedidos : [],
        totalPedidos: Number(res?.totalPedidos || 0),
        actualizadoEn: res?.actualizadoEn || new Date().toISOString(),
      });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo cargar el dashboard");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fecha]);

  useEffect(() => {
    if (!open) return undefined;
    cargar();
    const id = setInterval(() => cargar({ silent: true }), POLL_MS);
    return () => clearInterval(id);
  }, [open, cargar]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const grupos = data.grupos || [];
    if (!grupos.length) {
      setSelectedHora(null);
      return;
    }
    if (!selectedHora || !grupos.some((slot) => slot.horaRetiro === selectedHora)) {
      setSelectedHora(grupos[0].horaRetiro);
    }
  }, [data.grupos, open, selectedHora]);

  const topSlots = useMemo(() => {
    return [...(data.grupos || [])]
      .sort((a, b) => Number(b.cantidad || 0) - Number(a.cantidad || 0))
      .slice(0, 3);
  }, [data.grupos]);

  const lastUpdate = data.actualizadoEn
    ? new Date(data.actualizadoEn).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })
    : "--:--";

  const selectedSlot = useMemo(() => {
    return (data.grupos || []).find((slot) => slot.horaRetiro === selectedHora) || null;
  }, [data.grupos, selectedHora]);

  const selectedPedido = useMemo(() => {
    const pedidos = selectedSlot?.pedidos || [];
    const pedido = pedidos.find((item) => item.id === selectedPedidoId) || pedidos[0] || null;
    if (!pedido) return null;
    return normalizePedido(pedidoDetails[pedido.id], pedido);
  }, [pedidoDetails, selectedPedidoId, selectedSlot]);

  useEffect(() => {
    const pedidos = selectedSlot?.pedidos || [];
    if (!pedidos.length) {
      setSelectedPedidoId(null);
      return;
    }
    if (!pedidos.some((pedido) => pedido.id === selectedPedidoId)) {
      setSelectedPedidoId(pedidos[0].id);
    }
  }, [selectedPedidoId, selectedSlot]);

  useEffect(() => {
    if (!open || !selectedSlot?.pedidos?.length) return undefined;
    let cancelled = false;
    const pedidos = selectedSlot.pedidos.filter((pedido) => pedido?.id && !pedidoDetails[pedido.id]);
    if (!pedidos.length) return undefined;

    Promise.all(
      pedidos.map(async (pedido) => {
        try {
          const { data: res } = await api.get(`/api/pedidos/${pedido.id}`);
          return [pedido.id, normalizePedido(res?.pedido, pedido)];
        } catch {
          return null;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      const validEntries = entries.filter(Boolean);
      if (!validEntries.length) return;
      setPedidoDetails((current) => ({
        ...current,
        ...Object.fromEntries(validEntries),
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [open, pedidoDetails, selectedSlot]);

  if (!open) return null;

  return (
    <div className="fdp-backdrop" onMouseDown={onClose}>
      <section className="fdp-panel" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Horarios disponibles">
        <header className="fdp-head">
          <div>
            <span>Horarios disponibles</span>
            <strong>{data.totalPedidos} pedidos</strong>
          </div>
          <button type="button" className="fdp-close" onClick={onClose} aria-label="Cerrar">x</button>
        </header>

        <div className="fdp-toolbar">
          <div className="fdp-tabs">
            <button type="button" className={mode === "today" ? "active" : ""} onClick={() => setMode("today")}>Hoy</button>
            <button type="button" className={mode === "tomorrow" ? "active" : ""} onClick={() => setMode("tomorrow")}>Mañana</button>
            <button type="button" className={mode === "custom" ? "active" : ""} onClick={() => setMode("custom")}>Fecha</button>
          </div>
          <input
            type="date"
            value={customDate}
            onChange={(e) => { setCustomDate(e.target.value); setMode("custom"); }}
          />
          <button type="button" className="fdp-refresh" onClick={() => cargar()} disabled={loading}>Actualizar</button>
        </div>

        {error && <div className="fdp-alert">{error}</div>}

        <div className="fdp-body">
          {loading ? (
            <div className="fdp-empty">Cargando horarios...</div>
          ) : data.grupos.length ? (
            <div className="fdp-grid">
              {data.grupos.map((slot) => {
                const demand = demandInfo(Number(slot.cantidad || 0));
                const selected = slot.horaRetiro === selectedHora;
                return (
                  <button
                    type="button"
                    className={`fdp-slot fdp-slot--${demand.key}${selected ? " is-selected" : ""}`}
                    key={slot.horaRetiro}
                    onClick={() => setSelectedHora(slot.horaRetiro)}
                    aria-pressed={selected}
                  >
                    <div>
                      <span>Retiro</span>
                      <strong>{slot.horaRetiro}</strong>
                    </div>
                    <div className="fdp-count">
                      <b>{slot.cantidad}</b>
                      <small>{slot.cantidad === 1 ? "pedido" : "pedidos"}</small>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="fdp-empty">No hay pedidos con hora de retiro para esta fecha.</div>
          )}

          {selectedSlot && (
            <section className="fdp-detail" aria-live="polite">
              <div className="fdp-detail__head">
                <span>Detalle del pedido - {selectedSlot.horaRetiro}</span>
                <strong>{selectedPedido ? comandaLabel(selectedPedido) : `${selectedSlot.cantidad} pedidos`}</strong>
              </div>

              {(selectedSlot.pedidos || []).length > 1 && (
                <div className="fdp-picker">
                  <div className="fdp-picker__head">Pedidos del horario {selectedSlot.horaRetiro}</div>
                  <div className="fdp-picker__list">
                    {(selectedSlot.pedidos || []).map((pedido) => {
                      const pedidoInfo = normalizePedido(pedidoDetails[pedido.id], pedido);
                      const selected = pedido.id === selectedPedido?.id;
                      return (
                        <button
                          type="button"
                          className={`fdp-picker__item${selected ? " is-selected" : ""}`}
                          key={pedido.id}
                          onClick={() => setSelectedPedidoId(pedido.id)}
                          aria-pressed={selected}
                        >
                          <span className="fdp-picker__check">{selected ? "✓" : ""}</span>
                          <span className="fdp-picker__main">
                            <b>{comandaLabel(pedidoInfo)}</b>
                            <strong>{textOrDash(pedidoInfo.cliente)}</strong>
                            {pedidoInfo.canal && <small>{pedidoInfo.canal}</small>}
                          </span>
                          <span className="fdp-picker__total">{fmtMoney(pedidoInfo.totalNeto ?? pedidoInfo.total)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedPedido && (
                <div className="fdp-detail__orders">
                  <article className="fdp-order" key={selectedPedido.id}>
                    <div className="fdp-order__meta">
                      <div>
                        <span>Cliente</span>
                        <strong>{textOrDash(selectedPedido.cliente)}</strong>
                      </div>
                      <div>
                        <span>Teléfono</span>
                        <strong>{textOrDash(selectedPedido.telefono)}</strong>
                      </div>
                      <div>
                        <span>Canal</span>
                        <strong>{textOrDash(selectedPedido.canal)}</strong>
                      </div>
                      <div>
                        <span>Hora de retiro</span>
                        <strong>{textOrDash(selectedPedido.horaRetiro)}</strong>
                      </div>
                    </div>

                    <div className="fdp-order__note">
                      <span>Observaciones</span>
                      <p>{textOrDash(selectedPedido.observacion)}</p>
                    </div>

                    <div className="fdp-products">
                      <span>Productos</span>
                      {Array.isArray(selectedPedido.items) && selectedPedido.items.length ? (
                        <ul>
                          {selectedPedido.items.map((item, idx) => (
                            <li key={item.id || `${selectedPedido.id}-${idx}`}>
                              <b>{Number(item.cantidad || 1)}x</b>
                              <span>{textOrDash(item.nombre)}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p>Sin productos registrados.</p>
                      )}
                    </div>

                    <div className="fdp-order__totals">
                      <div>
                        <span>Total</span>
                        <strong>{fmtMoney(selectedPedido.totalNeto ?? selectedPedido.total)}</strong>
                      </div>
                      <div>
                        <span>Estado de pago</span>
                        <strong>{paymentText(selectedPedido)}</strong>
                      </div>
                    </div>
                  </article>
                </div>
              )}
            </section>
          )}
        </div>

        <footer className="fdp-foot">
          <span>Mayor demanda</span>
          <div>
            {topSlots.length ? topSlots.map((slot) => (
              <b key={slot.horaRetiro}>{slot.horaRetiro} ({slot.cantidad})</b>
            )) : <b>Sin horarios</b>}
          </div>
          <small>Actualizado {lastUpdate}</small>
        </footer>
      </section>
    </div>
  );
}
